import React, { useRef, useCallback, useMemo } from 'react';
import { KiteCard, KiteAlert } from '@kite/react-kite';
import { ITCOForm, ITCOSubmission } from 'types';
import { useTCORenderFields } from 'hooks';
import { tcoFieldsIds } from 'utils/defaultsAndConstants/constants';
import SpectrumDiyTotal from 'components/TCOComponents/TCOOverview/SpectrumDiyTotal';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import './TCOWiringCosts.scss';

export interface ITCOWiringCostsProps {
  form: ITCOForm;
  tcoSubmissions: ITCOSubmission[];
}

const TCOImplementationCostsWiringCosts = ({
  form,
  tcoSubmissions,
}: ITCOWiringCostsProps) => {
  const { name, fields, id } = form;
  const ref = useRef<HTMLFormElement>(null);

  const { fieldComponents, fieldValues, updateTcoSubmissionsError } =
    useTCORenderFields({
      fields,
      tcoSubmissions,
      formId: id,
    });

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  }, []);

  const {
    totalSpectrumWiringCost,
    totalSpectrumScissorLiftCost,
    totalDiyWiringCost,
    totalDiyScissorLiftCost,
  } = useMemo(() => {
    const {
      qtyOfDropsFieldId,
      qtyOfScissorLiftRentalsFieldId,
      costPerDropSpectrumFieldId,
      costPerDropDiyFieldId,
      scissorLiftCostSpectrumFieldId,
      scissorLiftCostDiyFieldId,
    } = tcoFieldsIds;

    const qtyOfDrops = parseInt(fieldValues[qtyOfDropsFieldId] || '0');
    const qtyOfScissorLiftRentals = parseInt(
      fieldValues[qtyOfScissorLiftRentalsFieldId] || '0'
    );
    const costPerDropSpectrum = parseFloat(
      fieldValues[costPerDropSpectrumFieldId] || '0'
    );
    const costPerDropDiy = parseFloat(
      fieldValues[costPerDropDiyFieldId] || '0'
    );
    const scissorLiftCostSpectrum = parseFloat(
      fieldValues[scissorLiftCostSpectrumFieldId] || '0'
    );
    const scissorLiftCostDiy = parseFloat(
      fieldValues[scissorLiftCostDiyFieldId] || '0'
    );

    return {
      totalSpectrumWiringCost: qtyOfDrops * costPerDropSpectrum,
      totalSpectrumScissorLiftCost:
        qtyOfScissorLiftRentals * scissorLiftCostSpectrum,
      totalDiyWiringCost: qtyOfDrops * costPerDropDiy,
      totalDiyScissorLiftCost: qtyOfScissorLiftRentals * scissorLiftCostDiy,
    };
  }, [fieldValues]);

  return (
    <KiteCard className="tco-wiring-costs">
      <h3 className="tco-wiring-costs__title">{name}</h3>
      <div className="implementation-costs">
        {updateTcoSubmissionsError && (
          <KiteAlert
            type="alert"
            title="Save Error"
            description="Something went wrong saving your answers. Any changes will not be saved. Please refresh and try again later."
          />
        )}
        <form ref={ref} onSubmit={handleSubmit}>
          <div className="implementation-costs__qty-fields">
            {fieldComponents.slice(0, 2)}
          </div>
          <div className="implementation-costs__wiring-container">
            <div className="implementation-costs__wiring-spectrum">
              <h4 className="implementation-costs__section-title">SPECTRUM</h4>
              <div className="implementation-costs__inputs">
                {fieldComponents[2]}
                {fieldComponents[4]}
              </div>
            </div>
            <div className="implementation-costs__vertical-divider"></div>
            <div className="implementation-costs__wiring-diy">
              <h4 className="implementation-costs__section-title">DIY</h4>
              <div className="implementation-costs__inputs">
                {fieldComponents[3]}
                {fieldComponents[5]}
              </div>
            </div>
          </div>
          <div className="implementation-costs__totals">
            <SpectrumDiyTotal
              title="Spectrum Total"
              primaryCostLabel="Total Wiring Cost"
              primaryCost={`$${tcoFormattedPrice(totalSpectrumWiringCost)}`}
              secondaryCostLabel="Total Scissor Lift Cost"
              secondaryCost={`$${tcoFormattedPrice(
                totalSpectrumScissorLiftCost
              )}`}
            />
            <SpectrumDiyTotal
              title="DIY Total"
              primaryCostLabel="Total Wiring Cost"
              primaryCost={`$${tcoFormattedPrice(totalDiyWiringCost)}`}
              secondaryCostLabel="Total Scissor Lift Cost"
              secondaryCost={`$${tcoFormattedPrice(totalDiyScissorLiftCost)}`}
              isDiy={true}
            />
          </div>
        </form>
      </div>
    </KiteCard>
  );
};

export default TCOImplementationCostsWiringCosts;
